import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import {
  Alert,
  Button,
  ButtonLink,
  Card,
  ErrorState,
  Skeleton,
  SkeletonGroup,
} from '@/components/ui';
import { BindingPicker } from '@/features/pricing/BindingPicker';
import { CoverColourPicker } from '@/features/pricing/CoverColourPicker';
import { CopiesField } from '@/features/pricing/CopiesField';
import { parseCopies } from '@/features/pricing/copies';
import { PresetPicker } from '@/features/pricing/PresetPicker';
import { PriceBreakdown } from '@/features/pricing/PriceBreakdown';
import { useAnnounceOnChange } from '@/lib/a11y';
import { useMeta } from '@/lib/api/meta';
import { useIsDesktop } from '@/lib/hooks/useMediaQuery';
import { queryKeys, useApiMutation } from '@/lib/api/queryClient';
import { createQuote, useBindingPrices, useOptionPrices, useQuote } from '@/lib/api/quotes';
import type { QuoteOut } from '@/lib/api/types';
import { formatPages, formatPaise } from '@/lib/format';
import { paths } from '@/routes/paths';

import { documentsFromQuote, otherDocuments, withDocument } from '@/features/pricing/basket';

import { stepUrl } from './steps';
import { useOrderStep } from './useOrderStep';

/**
 * /order/options — paper, colour, sides, copies.
 *
 * Every number on this screen comes from POST /api/v1/quotes/options, which
 * prices all four presets in one call. The client never multiplies a per-page
 * rate by a page count: the server's integers are the only prices that exist,
 * and the one the customer picks is the one the quote is created from.
 *
 * Selection is recovered rather than remembered. Arriving here with a `?q=` (the
 * customer pressed Back from a later step) reads the preset and copy count off
 * the stored quote, so the screen agrees with the server instead of with
 * whatever a component happened to keep.
 */
export function OptionsStep() {
  const { step, stepper, urlState } = useOrderStep();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const uploadId = urlState.upload;

  const existingQuote = useQuote(urlState.quote);
  const meta = useMeta();
  const isDesktop = useIsDesktop();

  // The basket so far. The quote IS the basket (see features/pricing/basket),
  // so "the other documents" are its items minus the one on this screen.
  const basket = documentsFromQuote(existingQuote.data);
  const others = otherDocuments(basket, uploadId);
  const method = existingQuote.data?.fulfilment_method ?? 'delivery';

  // What this file was set to last time, if it is already in the basket. That
  // is what makes going back and changing the paper on the second document work.
  const existingItem = existingQuote.data?.items.find((item) => item.upload_id === uploadId);

  // The URL first, then what this document is already set to in the basket.
  // The fallback is what makes the cart's "Edit" honest when the link has no
  // `?c=`: without it a three-copy document would come back as one copy and
  // Continue would quietly write that back.
  const copies = urlState.copies ? parseCopies(urlState.copies) : (existingItem?.copies ?? 1);

  const [chosenSlug, setChosenSlug] = useState<string | null>(null);

  // The binding lives in the URL rather than in state, like `copies`: it is a
  // choice the customer made, and until Continue turns it into a quote row the
  // URL is the only place that survives a refresh.
  const bindingSlug = urlState.binding ?? null;

  // In the URL for the same reason as the binding: it is a choice the customer
  // made, and until Continue turns it into a quote item the URL is the only
  // place that survives a refresh.
  const chosenCoverColour = urlState.cover ?? null;

  const prices = useOptionPrices({
    uploadId,
    copies,
    bindingSlug,
    others,
    fulfilmentMethod: method,
  });

  const options = prices.data?.options ?? [];
  const fromQuote = options.find((entry) => entry.option.id === existingItem?.print_option_id);
  const selected =
    options.find((entry) => entry.option.slug === chosenSlug) ?? fromQuote ?? options[0];

  // Priced against the selected preset: duplex halves the sheets in a book, so
  // which bindings physically fit depends on how it is being printed.
  const bindingPrices = useBindingPrices({
    uploadId,
    printOptionSlug: selected?.option.slug,
    copies,
    others,
    fulfilmentMethod: method,
  });
  const bindings = bindingPrices.data?.bindings ?? [];

  // What the picker shows as selected, in order of authority: the URL, then the
  // quote we are returning to, then the catalogue default.
  const selectedBinding =
    bindings.find((entry) => entry.binding.slug === bindingSlug) ??
    bindings.find((entry) => entry.binding.id === existingItem?.binding_option_id) ??
    bindings.find((entry) => entry.binding.is_default);

  const setBinding = useCallback(
    (slug: string) => {
      navigate(stepUrl('options', { ...urlState, binding: slug }), { replace: true });
    },
    [navigate, urlState],
  );

  const setCoverColour = useCallback(
    (slug: string) => {
      navigate(stepUrl('options', { ...urlState, cover: slug }), { replace: true });
    },
    [navigate, urlState],
  );

  // Changing copies rewrites the URL rather than setState, so a refresh — or a
  // shared link — comes back with the same basket. `replace` keeps the Back
  // button meaning "previous step", not "previous copy count".
  const setCopies = useCallback(
    (next: number) => {
      navigate(stepUrl('options', { ...urlState, copies: String(next) }), { replace: true });
    },
    [navigate, urlState],
  );

  // Only bindings with a cover take a colour. Sending one for a binding
  // without a cover is refused by the server rather than quietly dropped, so
  // the client must not send one either.
  const takesCoverColour = selectedBinding?.binding.has_cover ?? false;
  const coverPalette = meta.data?.cover_colours ?? [];
  const coverColour = takesCoverColour
    ? (chosenCoverColour ??
      existingItem?.cover_colour ??
      meta.data?.default_cover_colour ??
      coverPalette[0]?.slug ??
      null)
    : null;

  const quoteMutation = useApiMutation<QuoteOut, void>({
    mutationFn: () =>
      // The whole basket every time, with this document's choices applied. A
      // quote is re-priced from scratch server-side anyway, and re-pricing all
      // of it is what has to happen: delivery is quoted once for the parcel.
      createQuote({
        documents: withDocument(basket, {
          upload_id: uploadId ?? '',
          print_option_slug: selected?.option.slug ?? '',
          copies,
          ...(selectedBinding ? { binding_slug: selectedBinding.binding.slug } : {}),
          ...(coverColour ? { cover_colour: coverColour } : {}),
        }),
        fulfilment_method: method,
      }),
    onSuccess: (quote) => {
      queryClient.setQueryData(queryKeys.quote(quote.id), quote);
      // The badge in the header reads the basket, and this just changed it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.currentBasket() });

      // With more than one document the cart is the only screen that shows
      // them all, so it is where "I have just added another file" belongs.
      // A single-document order goes straight on, as it always has.
      navigate(
        quote.items.length > 1 ? paths.cart : stepUrl('details', { ...urlState, quote: quote.id }),
      );
    },
  });

  useAnnounceOnChange(
    selected && !prices.isFetching
      ? `${selected.option.label}${
          selectedBinding && selectedBinding.binding_cost_paise > 0
            ? ` with ${selectedBinding.binding.label.toLowerCase()}`
            : ''
        }: ${formatPaise(selected.totals.total_paise)} for ${formatPages(prices.data?.page_count ?? 0)}, ${copies === 1 ? '1 copy' : `${copies} copies`}.`
      : null,
  );

  const rejectedUpload = prices.error?.code === 'validation_rejected';

  return (
    <AppShell
      title={step.title}
      subtitle="Pick how it should be printed. Prices update as you choose."
      progress={stepper}
      backTo={stepUrl('review', urlState)}
      backLabel="Back to the file check"
      aside={
        selected ? (
          // On desktop the breakdown lives beside the choices rather than under
          // them: changing the paper and watching the total move is the whole
          // point of this screen, and on a wide window the two were a scroll
          // apart. It is rendered ONCE — see the `isDesktop` guard below, which
          // drops it from the flow when the rail has it.
          <PriceBreakdown breakdown={selected.breakdown} totals={selected.totals} defaultOpen />
        ) : undefined
      }
      action={
        options.length > 0 ? (
          <Button
            fullWidth
            disabled={!selected || selectedBinding?.is_available === false}
            loading={quoteMutation.isPending}
            loadingText="Saving your choice…"
            onClick={() => quoteMutation.mutate()}
          >
            Continue
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {quoteMutation.isError && quoteMutation.error ? (
          <Alert severity="danger" title="We could not save that choice" live>
            <p>{quoteMutation.error.message}</p>
          </Alert>
        ) : null}

        {prices.isError && prices.error ? (
          rejectedUpload ? (
            // The server refuses to price a file it has already rejected. Say so
            // in its words and offer the only move that helps.
            <Alert severity="danger" title="We cannot print this file" live>
              <p>{prices.error.message}</p>
              <div className="mt-3">
                <ButtonLink to={stepUrl('upload', { ...urlState, upload: undefined })} fullWidth>
                  Upload a different file
                </ButtonLink>
              </div>
            </Alert>
          ) : (
            <ErrorState
              error={prices.error}
              onRetry={() => void prices.refetch()}
              retrying={prices.isFetching}
              title="We could not load prices"
            />
          )
        ) : prices.isPending ? (
          <SkeletonGroup label="Loading prices">
            <div className="flex flex-col gap-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          </SkeletonGroup>
        ) : (
          <>
            <Card title="Your document">
              <p className="text-sm text-ink-muted">
                {formatPages(prices.data.page_count)} &middot;{' '}
                {copies === 1 ? '1 copy' : `${copies} copies`}
              </p>
              <div className="mt-3">
                <CopiesField value={copies} onChange={setCopies} disabled={prices.isFetching} />
              </div>
            </Card>

            {/* Above the presets, not inside them: this is a fact about the
                customer's FILE, and they should read it before choosing rather
                than discover it on whichever card they happen to look at. The
                cards repeat it, because with mixed stock the presets can differ
                on what they will do.

                The customer's own size is deliberately NOT named. "A5" is our
                vocabulary, not theirs, and it is not the actionable half — what
                they need is that it is not the paper we print on. The size is
                still carried through to the bench, which does need it. */}
            {selected?.document_size_label ? (
              <Alert severity="warning" title={`This file is not ${selected.size_label}`}>
                <p>We print on {selected.size_label}, so the pages are resized to fit.</p>
              </Alert>
            ) : null}

            <PresetPicker
              options={options}
              value={selected?.option.slug ?? null}
              onChange={setChosenSlug}
              refreshing={prices.isFetching}
            />

            {/* Between the presets and the breakdown on purpose: binding is a
                decision about the finished book, so it only makes sense once
                the customer has settled how it is printed — and its cost has to
                land in the breakdown directly below. */}
            <BindingPicker
              bindings={bindings}
              value={selectedBinding?.binding.slug ?? null}
              onChange={setBinding}
              sheetsPerCopy={bindingPrices.data?.sheets_per_copy ?? 0}
              copies={copies}
              refreshing={bindingPrices.isFetching}
            />

            {/* Directly under the binding, because it is part of that choice
                rather than a separate one — and it appears only when the
                chosen binding actually has a cover. */}
            {takesCoverColour ? (
              <CoverColourPicker
                colours={coverPalette}
                value={coverColour}
                onChange={setCoverColour}
                bindingLabel={selectedBinding?.binding.label}
              />
            ) : null}

            {selected && !isDesktop ? (
              <PriceBreakdown breakdown={selected.breakdown} totals={selected.totals} />
            ) : null}

            <p className="text-sm text-ink-subtle">
              Delivery is worked out on the next screens, once we know where it is going.
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
}
